import { useState } from 'react';
import { Eye, EyeOff, TriangleAlert } from 'lucide-react';

/**
 * Spec 003 — shared form primitives for the auth screens. Same hand-rolled Tailwind conventions as
 * the membership ApplicationForm (brand tokens, focus-visible outline, aria wiring) so auth looks and
 * behaves identically. Kept in one module the auth pages share; ApplicationForm is left untouched to
 * avoid churning its visual-regression baselines (a future refactor can dedupe once baselines regen).
 */

export const inputClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 font-sans text-sm text-ink placeholder:text-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60';

export function FieldError({ id, error }: { id: string; error?: string }) {
  if (!error) return null;
  return (
    <p id={id} role="alert" className="flex items-center gap-1.5 font-sans text-xs text-error">
      <TriangleAlert aria-hidden className="h-3.5 w-3.5" />
      {error}
    </p>
  );
}

export function TextField(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const errId = `${props.id}-error`;
  const hintId = `${props.id}-hint`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={props.id} className="block font-sans text-sm font-medium text-ink">
        {props.label}
        {props.required && <span className="text-error"> *</span>}
      </label>
      {props.hint && (
        <p id={hintId} className="font-sans text-xs text-muted">
          {props.hint}
        </p>
      )}
      <input
        id={props.id}
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        autoComplete={props.autoComplete}
        disabled={props.disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={props.autoFocus}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={
          [props.hint ? hintId : null, props.error ? errId : null].filter(Boolean).join(' ') || undefined
        }
        className={inputClass}
      />
      <FieldError id={errId} error={props.error} />
    </div>
  );
}

/** Password field with a show/hide toggle (accessible: labelled button, aria-pressed). */
export function PasswordField(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
  autoComplete?: string;
  disabled?: boolean;
}) {
  const [show, setShow] = useState(false);
  const errId = `${props.id}-error`;
  const hintId = `${props.id}-hint`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={props.id} className="block font-sans text-sm font-medium text-ink">
        {props.label}
      </label>
      {props.hint && (
        <p id={hintId} className="font-sans text-xs text-muted">
          {props.hint}
        </p>
      )}
      <div className="relative">
        <input
          id={props.id}
          type={show ? 'text' : 'password'}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          autoComplete={props.autoComplete}
          disabled={props.disabled}
          aria-invalid={props.error ? true : undefined}
          aria-describedby={
            [props.hint ? hintId : null, props.error ? errId : null].filter(Boolean).join(' ') || undefined
          }
          className={`${inputClass} pr-10`}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-pressed={show}
          aria-label={show ? 'Hide password' : 'Show password'}
          disabled={props.disabled}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {show ? <EyeOff aria-hidden className="h-4 w-4" /> : <Eye aria-hidden className="h-4 w-4" />}
        </button>
      </div>
      <FieldError id={errId} error={props.error} />
    </div>
  );
}
